import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { ASSISTANT_ROUTES } from "../../../routes/patientRoutes/routePaths";
import useAssistantManagement from "../../../assistantDashboard/hooks/useAssistantManagement";
import "./AssistantInvitationActivation.css";

export default function AssistantInvitationActivation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [status, setStatus] = useState("verifying");
  const [errorMessage, setErrorMessage] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assistantEmail, setAssistantEmail] = useState("");
  const { verifyActivationToken, setActivationPassword, login } = useAssistantManagement();

  const isMountedRef = useRef(true);
  useEffect(() => () => { isMountedRef.current = false; }, []);

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setStatus("error");
        setErrorMessage(t("assistantPages.invitation.linkExpired"));
        return;
      }

      try {
        const data = await verifyActivationToken(token).unwrap();

        // 🔍 DEBUG TEMPORAIRE — à retirer une fois le bug identifié
        console.log("🔍 [verify] réponse complète:", data);
        console.log("🔍 [verify] data.email:", data?.email);
        console.log("🔍 [verify] data.assistant?.email:", data?.assistant?.email);

        const email = data?.email || data?.assistant?.email || "";
        if (isMountedRef.current) {
          setAssistantEmail(email);
          setStatus("ready");
        }
      } catch (error) {
        console.error("🔍 [verify] ÉCHEC:", error);
        if (isMountedRef.current) {
          setStatus("error");
          setErrorMessage(String(error?.message || t("assistantPages.invitation.linkExpired")));
        }
      }
    };

    verify();
  }, [token, t]);

  const rules = useMemo(
    () => ({
      length: password.length >= 8,
      hasUpper: /[A-Z]/.test(password),
      hasLower: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[^A-Za-z0-9]/.test(password),
      matches: password && confirmPassword && password === confirmPassword,
    }),
    [password, confirmPassword]
  );

  const allRulesOk = Object.values(rules).every(Boolean);

  const onSubmit = async (event) => {
    event.preventDefault();

    if (!rules.matches) {
      message.error(t("assistantPages.invitation.passwordMismatch"));
      return;
    }

    if (!allRulesOk) {
      message.error(t("assistantPages.invitation.passwordWeak"));
      return;
    }

    setIsSubmitting(true);

    try {
      const setPasswordResult = await setActivationPassword({
        token,
        password,
        confirmPassword,
      }).unwrap();

      // 🔍 DEBUG TEMPORAIRE
      console.log("🔍 [set-password] réponse complète:", setPasswordResult);
      console.log("🔍 [set-password] assistantEmail (state récupéré au verify):", assistantEmail);

      if (assistantEmail) {
        try {
          const loginResult = await login({ email: assistantEmail, password }).unwrap();
          console.log("🔍 [login auto] SUCCÈS:", loginResult);
        } catch (loginError) {
          console.error("🔍 [login auto] ÉCHEC:", loginError);
          // L'activation a réussi même si l'auto-login échoue ;
          // on redirige vers le login manuel dans ce cas seulement.
          setStatus("success");
          message.success(t("assistantPages.invitation.successMessage"));
          setIsSubmitting(false);
          setTimeout(() => {
            navigate(ASSISTANT_ROUTES.LOGIN, { replace: true });
          }, 1200);
          return;
        }
      } else {
        console.warn("🔍 Pas d'email récupéré au verify -> login auto impossible, redirection vers AVAILABILITY quand même");
      }

      // 🔍 DEBUG TEMPORAIRE — état de la session juste avant la navigation
      console.log("🔍 localStorage.assistantAccessToken:", localStorage.getItem("assistantAccessToken"));
      console.log("🔍 localStorage.assistantToken:", localStorage.getItem("assistantToken"));
      console.log("🔍 localStorage.userRole:", localStorage.getItem("userRole"));
      console.log("🔍 document.cookie:", document.cookie);

      setStatus("success");
      message.success(t("assistantPages.invitation.successMessage"));

      setTimeout(() => {
        console.log("🔍 Navigation vers:", ASSISTANT_ROUTES.AVAILABILITY);
        navigate(ASSISTANT_ROUTES.AVAILABILITY, { replace: true });
      }, 1200);
    } catch (error) {
      console.error("🔍 [set-password] ÉCHEC:", error);
      setIsSubmitting(false);
      message.error(String(error?.message || t("assistantPages.invitation.submitError")));
    }
  };

  if (status === "verifying") {
    return (
      <div className="assistant-invitation-page">
        <div className="assistant-invitation-card">
          <div className="assistant-invitation-loader" />
          <p>{t("assistantPages.invitation.verifying")}</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="assistant-invitation-page">
        <div className="assistant-invitation-card assistant-invitation-card--state">
          <h2>{t("assistantPages.invitation.invalidLink")}</h2>
          <p>{errorMessage}</p>
          <button
            type="button"
            className="assistant-invitation-btn"
            onClick={() => navigate(ASSISTANT_ROUTES.LOGIN)}
          >
            {t("assistantPages.invitation.backToLogin")}
          </button>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="assistant-invitation-page">
        <div className="assistant-invitation-card assistant-invitation-card--state">
          <h2>{t("assistantPages.invitation.successTitle")}</h2>
          <p>{t("assistantPages.invitation.successMessage")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="assistant-invitation-page">
      <div className="assistant-invitation-card">
        <h1>{t("assistantPages.invitation.title")}</h1>
        <p>{t("assistantPages.invitation.subtitle")}</p>

        <form onSubmit={onSubmit} className="assistant-invitation-form">
          <label htmlFor="password">{t("assistantPages.invitation.newPassword")}</label>
          <div className="assistant-invitation-input-wrap">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="********"
            />
            <button
              type="button"
              className="assistant-invitation-ghost"
              onClick={() => setShowPassword((prev) => !prev)}
            >
              {showPassword ? t("assistantPages.invitation.hide") : t("assistantPages.invitation.show")}
            </button>
          </div>

          <label htmlFor="confirmPassword">{t("assistantPages.invitation.confirmPassword")}</label>
          <input
            id="confirmPassword"
            type={showPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            placeholder="********"
          />

          <ul className="assistant-invitation-rules">
            <li className={rules.length ? "ok" : ""}>{t("assistantPages.invitation.ruleLength")}</li>
            <li className={rules.hasUpper && rules.hasLower ? "ok" : ""}>{t("assistantPages.invitation.ruleCase")}</li>
            <li className={rules.hasNumber ? "ok" : ""}>{t("assistantPages.invitation.ruleNumber")}</li>
            <li className={rules.hasSpecial ? "ok" : ""}>{t("assistantPages.invitation.ruleSpecial")}</li>
            <li className={rules.matches ? "ok" : ""}>{t("assistantPages.invitation.ruleMatch")}</li>
          </ul>

          <button type="submit" className="assistant-invitation-btn" disabled={isSubmitting || !allRulesOk}>
            {isSubmitting ? t("assistantPages.invitation.submitting") : t("assistantPages.invitation.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}